import { z } from "zod"
import { isValidPhone } from "@/lib/utils/phone"
import { isValidZip } from "@/lib/utils/zip"

export const personalInfoSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(255),
  lastName: z.string().trim().min(1, "Last name is required").max(255),
})

export const TEAM_TYPE_VALUES = ["Dealership", "Bodyshop", "DealershipGroup"] as const
export type OnboardingTeamType = (typeof TEAM_TYPE_VALUES)[number]

// Step 2a — identity. Only what tells us *who* the workspace is. Address
// fields live on step 2b; splitting the schema lets each step validate
// independently without each having to know about the other.
export const companyIdentitySchema = z.object({
  type: z.enum(TEAM_TYPE_VALUES, { message: "Pick your organization type" }),
  name: z.string().trim().min(1, "Business name is required").max(255),
})

// Step 2b — location.
export const companyLocationSchema = z.object({
  street: z.string().trim().min(1, "Street is required").max(255),
  city: z.string().trim().min(1, "City is required").max(255),
  state: z.string().trim().min(1, "State is required"),
  zipCode: z
    .string()
    .min(1, "ZIP is required")
    .refine(isValidZip, "Enter a valid ZIP (e.g. 12345 or 12345-6789)"),
  // US-only product policy: schema rejects any other country so UI changes
  // can't bypass the constraint. Forms render a disabled pill, not an input.
  country: z.literal("United States"),
  phoneNumber: z
    .string()
    .optional()
    .refine((v) => !v || isValidPhone(v), "Enter a valid phone number"),
})

// Combined for the team-creation API call at the end of step 2b. Kept
// available so callers (or future tests) can validate the whole company
// payload in one shot.
export const companyInfoSchema = companyIdentitySchema.merge(companyLocationSchema)

export type PersonalInfoData = z.infer<typeof personalInfoSchema>
export type CompanyIdentityData = z.infer<typeof companyIdentitySchema>
export type CompanyLocationData = z.infer<typeof companyLocationSchema>
export type CompanyInfoData = z.infer<typeof companyInfoSchema>
